import { FeatureFlag } from '@parsleyai/feature-flags'
import { BottomNav, type BottomNavItem } from '@parsleyai/ui'
import { BookOpen, BowlFood, CalendarDots, Gear, type Icon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useDesignVariant } from '@/experiments/meal-v2/useDesignVariant'
import { useHideOnScroll } from '@/lib/useHideOnScroll'
import { useIsMobile } from '@/lib/useIsMobile'
import { useAuth, useGetPrimaryAction } from '@/store'
import { useFeatureFlag } from '@/store/featureFlags/useFeatureFlag'

// Home is intentionally NOT a tab: the top bar's Parsley logo links to /home (the standard
// way to reach a home page), and dropping it de-crowds the bar to four wider touch targets.
// Order mirrors the desktop Navbar's tabs (Meal Plans · Recipes · Eating Style), with Settings —
// which lives in the desktop avatar menu — last.
const TABS: { path: string; icon: Icon; exact: boolean; labelKey: string }[] = [
  { path: '/meal-plans', icon: CalendarDots, exact: false, labelKey: 'nav.mealPlansShort' },
  { path: '/recipes', icon: BookOpen, exact: false, labelKey: 'nav.recipes' },
  { path: '/eating-style', icon: BowlFood, exact: false, labelKey: 'nav.myDiet' },
  { path: '/settings', icon: Gear, exact: false, labelKey: 'nav.settings' }
]

// Off-app routes (invite gate + auth) render their own full-screen chrome — no tab bar.
const OFF_APP_ROUTES = new Set(['/login', '/signup', '/no-invite'])

// The mobile bottom navigation. This is the web CONTAINER: it wires the real app state — auth +
// route guards, scroll-collapse, route-based active tab, the page's primary action, and react-router
// links — into the presentational `BottomNav` block (@parsleyai/ui), which owns the layout, motion,
// and the beUI press feel. `md:hidden` gates it to mobile (display:none removes the block's fixed
// layers on desktop, where the green Navbar takes over).
export default function BottomTabBar() {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const { t } = useTranslation()
  const designVariant = useDesignVariant()
  const mealPageRedesign = useFeatureFlag(FeatureFlag.MealPageRedesign)
  const isMobile = useIsMobile()
  // Shrink-on-scroll (quick-return): scrolling down past the threshold collapses the labeled bar
  // into a centered dots pill; scrolling up springs it back. Gated to mobile (the bar is md:hidden).
  const { hidden: collapsed } = useHideOnScroll(isMobile)
  const action = useGetPrimaryAction()

  // The block's action config from the page's primary action. Memoized on `action` (a stable context
  // value that only changes when a page sets/clears its action) so the FAB's retained-fade inside the
  // block isn't re-triggered on unrelated re-renders.
  const actionConfig = useMemo(
    () =>
      action ? { icon: <action.icon size={24} weight={action.weight ?? 'bold'} />, label: action.label, onClick: action.onClick } : null,
    [action]
  )

  if (!isAuthenticated) return null
  if (OFF_APP_ROUTES.has(location.pathname)) return null
  // Cooking mode is a full-screen takeover with its own chrome — hide the global tab bar.
  if (location.pathname.endsWith('/cooking')) return null
  // So is building a plan.
  if (location.pathname === '/meal-plans/new') return null
  // The editorial meal view (v2) is full-bleed with its own footer — hide the global tab bar there.
  if (!mealPageRedesign && designVariant === 'v2' && (location.pathname.startsWith('/cook/') || location.pathname.startsWith('/meal/')))
    return null

  const items: BottomNavItem[] = TABS.map(({ path, icon: Icon, exact, labelKey }) => ({
    id: path,
    icon: <Icon size={24} weight="fill" />,
    label: t(labelKey),
    active: exact ? location.pathname === path : location.pathname.startsWith(path),
    renderLink: ({ children, ...props }) => (
      <Link to={path} {...props}>
        {children}
      </Link>
    )
  }))

  return (
    <div className="md:hidden">
      <BottomNav items={items} collapsed={collapsed} action={actionConfig} />
    </div>
  )
}
